import { useState, useEffect, useMemo } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Types
interface Supplier {
  supplierName: string;
  supplierEmail: string | null;
  itemCount: number;
  lowStockCount: number;
}

interface InventoryItem {
  id: string;
  partNumber: string;
  description: string | null;
  supplierPartNumber: string | null;
  unitCost: number;
  onHand: number;
  reorderQuantity: number;
  minQuantity: number;
  supplierMinOrder: number;
  isLowStock?: boolean;
  suggestedQty?: number;
}

interface POItem {
  id?: string;
  inventoryItemId: string;
  partNumber: string;
  description: string | null;
  supplierPartNumber: string | null;
  supplierName: string;
  qtyOrdered: number;
  qtyReceived?: number;
  unitCost: number;
  lineTotal: number;
  onHand: number;
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierName: string;
  supplierEmail: string | null;
  supplierReference: string | null;
  status: 'draft' | 'submitted' | 'partial' | 'received' | 'cancelled';
  subtotal: number;
  taxAmount: number;
  shippingCost: number;
  totalAmount: number;
  notes: string | null;
  internalNotes: string | null;
  expectedDate: string | null;
  createdAt: string;
  submittedAt: string | null;
  receivedAt: string | null;
  itemCount?: number;
  items?: POItem[];
}

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
    submitted: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Submitted' },
    partial: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Partial' },
    received: { bg: 'bg-green-100', text: 'text-green-700', label: 'Received' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', c.bg, c.text)}>
      {c.label}
    </span>
  );
}

// Modal component
function Modal({ isOpen, onClose, title, children, size = 'lg' }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl' | 'full';
}) {
  if (!isOpen) return null;

  const sizeClass = {
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl',
  }[size];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className={`relative bg-white rounded-xl shadow-2xl ${sizeClass} w-full max-h-[90vh] overflow-hidden flex flex-col`}>
          <div className="border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6 overflow-y-auto flex-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

// PO Builder Component
function POBuilder({
  onSave,
  onCancel,
  editingPO,
}: {
  onSave: () => void;
  onCancel: () => void;
  editingPO?: PurchaseOrder;
}) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [supplierItems, setSupplierItems] = useState<InventoryItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [poItems, setPoItems] = useState<POItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [supplierEmail, setSupplierEmail] = useState('');

  // Load suppliers on mount
  useEffect(() => {
    apiRequest('/purchase-orders/suppliers')
      .then(setSuppliers)
      .catch(console.error);
  }, []);

  // Load existing PO data if editing
  useEffect(() => {
    if (editingPO?.items) {
      setPoItems(editingPO.items.map(item => ({
        ...item,
        supplierName: editingPO.supplierName,
        onHand: 0,
        lineTotal: item.qtyOrdered * item.unitCost,
      })));
      setNotes(editingPO.notes || '');
      setExpectedDate(editingPO.expectedDate?.split('T')[0] || '');
      setSupplierEmail(editingPO.supplierEmail || '');
    }
  }, [editingPO]);

  // Load supplier items when selected
  useEffect(() => {
    if (!selectedSupplier) {
      setSupplierItems([]);
      return;
    }
    setLoadingItems(true);
    apiRequest(`/purchase-orders/suppliers/${encodeURIComponent(selectedSupplier)}/items`)
      .then(items => {
        setSupplierItems(items);
        // Set supplier email
        const supplier = suppliers.find(s => s.supplierName === selectedSupplier);
        if (supplier?.supplierEmail) {
          setSupplierEmail(supplier.supplierEmail);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingItems(false));
  }, [selectedSupplier, suppliers]);

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return supplierItems;
    const q = searchQuery.toLowerCase();
    return supplierItems.filter(
      item =>
        item.partNumber.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        item.supplierPartNumber?.toLowerCase().includes(q)
    );
  }, [supplierItems, searchQuery]);

  // Add item to PO
  const addToPO = (item: InventoryItem, qty?: number) => {
    // Check if already in PO
    if (poItems.some(p => p.inventoryItemId === item.id)) {
      return;
    }
    const qtyOrdered = qty || item.suggestedQty || Math.max(1, item.reorderQuantity - item.onHand);
    setPoItems(prev => [
      ...prev,
      {
        inventoryItemId: item.id,
        partNumber: item.partNumber,
        description: item.description,
        supplierPartNumber: item.supplierPartNumber,
        supplierName: selectedSupplier,
        qtyOrdered,
        unitCost: item.unitCost,
        lineTotal: qtyOrdered * item.unitCost,
        onHand: item.onHand,
      },
    ]);
  };

  // Remove item from PO
  const removeFromPO = (inventoryItemId: string) => {
    setPoItems(prev => prev.filter(p => p.inventoryItemId !== inventoryItemId));
  };

  // Update item qty
  const updateItemQty = (inventoryItemId: string, qty: number) => {
    setPoItems(prev =>
      prev.map(p =>
        p.inventoryItemId === inventoryItemId
          ? { ...p, qtyOrdered: qty, lineTotal: qty * p.unitCost }
          : p
      )
    );
  };

  // Add all low stock items
  const addLowStockItems = async () => {
    if (!selectedSupplier) return;
    try {
      const lowStock = await apiRequest(
        `/purchase-orders/suppliers/${encodeURIComponent(selectedSupplier)}/low-stock`
      );
      lowStock.forEach((item: InventoryItem) => {
        if (!poItems.some(p => p.inventoryItemId === item.id)) {
          addToPO(item, item.suggestedQty);
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Group PO items by supplier
  const groupedItems = useMemo(() => {
    const groups: Record<string, POItem[]> = {};
    poItems.forEach(item => {
      if (!groups[item.supplierName]) {
        groups[item.supplierName] = [];
      }
      groups[item.supplierName].push(item);
    });
    return groups;
  }, [poItems]);

  // Calculate totals
  const totals = useMemo(() => {
    const bySupplier: Record<string, number> = {};
    let grandTotal = 0;
    poItems.forEach(item => {
      bySupplier[item.supplierName] = (bySupplier[item.supplierName] || 0) + item.lineTotal;
      grandTotal += item.lineTotal;
    });
    return { bySupplier, grandTotal };
  }, [poItems]);

  // Save PO
  const handleSave = async (submit = false) => {
    if (poItems.length === 0) {
      alert('Add at least one item to the purchase order');
      return;
    }

    setSaving(true);
    try {
      // Group by supplier and create separate POs
      const supplierNames = [...new Set(poItems.map(p => p.supplierName))];

      for (const supplierName of supplierNames) {
        const items = poItems.filter(p => p.supplierName === supplierName);
        const supplier = suppliers.find(s => s.supplierName === supplierName);

        const poData = {
          supplierName,
          supplierEmail: supplierName === selectedSupplier ? supplierEmail : supplier?.supplierEmail || '',
          expectedDate: expectedDate || undefined,
          notes,
          items: items.map(item => ({
            inventoryItemId: item.inventoryItemId,
            partNumber: item.partNumber,
            description: item.description,
            supplierPartNumber: item.supplierPartNumber,
            qtyOrdered: item.qtyOrdered,
            unitCost: item.unitCost,
          })),
        };

        if (editingPO && supplierNames.length === 1) {
          // Update existing PO
          await apiRequest(`/purchase-orders/${editingPO.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              notes,
              expectedDate: expectedDate || undefined,
              supplierEmail,
            }),
          });
        } else {
          // Create new PO
          const created = await apiRequest('/purchase-orders', {
            method: 'POST',
            body: JSON.stringify(poData),
          });

          if (submit) {
            await apiRequest(`/purchase-orders/${created.id}/submit`, {
              method: 'POST',
            });
          }
        }
      }

      onSave();
    } catch (err: any) {
      alert(err.message || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Supplier Selection */}
      <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Supplier</label>
            <select
              value={selectedSupplier}
              onChange={e => setSelectedSupplier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a supplier...</option>
              {suppliers.map(s => (
                <option key={s.supplierName} value={s.supplierName}>
                  {s.supplierName} ({s.itemCount} items, {s.lowStockCount} low)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Email</label>
            <input
              type="email"
              value={supplierEmail}
              onChange={e => setSupplierEmail(e.target.value)}
              placeholder="supplier@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Delivery</label>
            <input
              type="date"
              value={expectedDate}
              onChange={e => setExpectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Main Content - Two Panels */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
        {/* Left Panel - Available Parts */}
        <div className="flex flex-col border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Available Parts</h3>
            {selectedSupplier && (
              <button
                onClick={addLowStockItems}
                className="px-3 py-1 bg-amber-100 text-amber-700 rounded text-sm hover:bg-amber-200"
              >
                + Add Low Stock
              </button>
            )}
          </div>
          <div className="p-2 border-b">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search parts..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {!selectedSupplier ? (
              <p className="text-center text-gray-500 py-8">Select a supplier to view parts</p>
            ) : loadingItems ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : filteredItems.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No parts found</p>
            ) : (
              <div className="space-y-2">
                {filteredItems.map(item => {
                  const inPO = poItems.some(p => p.inventoryItemId === item.id);
                  return (
                    <div
                      key={item.id}
                      className={clsx(
                        'p-2 rounded border text-sm',
                        inPO ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-blue-300',
                        item.isLowStock && !inPO && 'border-l-4 border-l-red-400'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{item.partNumber}</div>
                          <div className="text-gray-500 truncate">{item.description}</div>
                          <div className="flex gap-4 mt-1 text-xs text-gray-400">
                            <span>On Hand: {item.onHand}</span>
                            <span>Reorder: {item.reorderQuantity}</span>
                            <span>${item.unitCost.toFixed(2)}</span>
                          </div>
                        </div>
                        {!inPO ? (
                          <button
                            onClick={() => addToPO(item)}
                            className="ml-2 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                          >
                            + Add
                          </button>
                        ) : (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                            Added
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - PO Draft */}
        <div className="flex flex-col border rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-4 py-2 border-b">
            <h3 className="font-semibold text-blue-800">Purchase Order</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {poItems.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                Add parts from the left panel to build your PO
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedItems).map(([supplier, items]) => (
                  <div key={supplier} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
                      <span className="font-medium text-gray-800">{supplier}</span>
                      <span className="text-sm text-gray-600">
                        ${totals.bySupplier[supplier]?.toFixed(2)}
                      </span>
                    </div>
                    <div className="divide-y">
                      {items.map(item => (
                        <div key={item.inventoryItemId} className="p-2 flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{item.partNumber}</div>
                            <div className="text-xs text-gray-500 truncate">{item.description}</div>
                          </div>
                          <div className="text-xs text-gray-400">
                            Stock: {item.onHand}
                          </div>
                          <input
                            type="number"
                            value={item.qtyOrdered}
                            onChange={e => updateItemQty(item.inventoryItemId, parseFloat(e.target.value) || 0)}
                            min={0}
                            className="w-20 px-2 py-1 border rounded text-sm text-center"
                          />
                          <div className="text-sm w-20 text-right">
                            ${item.lineTotal.toFixed(2)}
                          </div>
                          <button
                            onClick={() => removeFromPO(item.inventoryItemId)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals */}
          {poItems.length > 0 && (
            <div className="border-t bg-gray-50 p-3">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>${totals.grandTotal.toFixed(2)}</span>
              </div>
              {Object.keys(groupedItems).length > 1 && (
                <p className="text-xs text-gray-500 mt-1">
                  Multiple suppliers selected - will create {Object.keys(groupedItems).length} separate POs
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder="Order notes..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>

      {/* Actions */}
      <div className="mt-4 flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={() => handleSave(false)}
          disabled={saving || poItems.length === 0}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save as Draft'}
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={saving || poItems.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Submit PO'}
        </button>
      </div>
    </div>
  );
}

// Receive Items Modal
function ReceiveItemsModal({
  po,
  onClose,
  onReceived,
}: {
  po: PurchaseOrder;
  onClose: () => void;
  onReceived: () => void;
}) {
  const [items, setItems] = useState<Array<{ poItemId: string; qtyToReceive: number; notes: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (po.items) {
      setItems(
        po.items.map(item => ({
          poItemId: item.id!,
          qtyToReceive: item.qtyOrdered - (item.qtyReceived || 0),
          notes: '',
        }))
      );
    }
  }, [po.items]);

  const handleReceive = async () => {
    const itemsToReceive = items.filter(i => i.qtyToReceive > 0);
    if (itemsToReceive.length === 0) {
      alert('Enter quantities to receive');
      return;
    }

    setSaving(true);
    try {
      await apiRequest(`/purchase-orders/${po.id}/receive`, {
        method: 'POST',
        body: JSON.stringify({
          items: itemsToReceive.map(i => ({
            poItemId: i.poItemId,
            qtyReceived: i.qtyToReceive,
            notes: i.notes || undefined,
          })),
        }),
      });
      onReceived();
    } catch (err: any) {
      alert(err.message || 'Failed to receive items');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Enter quantities received for each item. Inventory will be updated automatically.
      </p>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2">Part #</th>
              <th className="text-left px-4 py-2">Description</th>
              <th className="text-center px-4 py-2">Ordered</th>
              <th className="text-center px-4 py-2">Received</th>
              <th className="text-center px-4 py-2">Remaining</th>
              <th className="text-center px-4 py-2">Receive Now</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {po.items?.map((item, idx) => {
              const remaining = item.qtyOrdered - (item.qtyReceived || 0);
              return (
                <tr key={item.id}>
                  <td className="px-4 py-2 font-medium">{item.partNumber}</td>
                  <td className="px-4 py-2 text-gray-600">{item.description}</td>
                  <td className="px-4 py-2 text-center">{item.qtyOrdered}</td>
                  <td className="px-4 py-2 text-center">{item.qtyReceived || 0}</td>
                  <td className="px-4 py-2 text-center font-medium text-amber-600">{remaining}</td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      min={0}
                      max={remaining}
                      value={items[idx]?.qtyToReceive || 0}
                      onChange={e =>
                        setItems(prev =>
                          prev.map((p, i) =>
                            i === idx ? { ...p, qtyToReceive: parseFloat(e.target.value) || 0 } : p
                          )
                        )
                      }
                      className="w-20 px-2 py-1 border rounded text-center"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={handleReceive}
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Receiving...' : 'Receive Items'}
        </button>
      </div>
    </div>
  );
}

// Main Page Component
export function PurchaseOrdersPage() {
  const [loading, setLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [viewingPO, setViewingPO] = useState<PurchaseOrder | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadPurchaseOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchQuery) params.set('search', searchQuery);
      params.set('limit', '100');

      const result = await apiRequest(`/purchase-orders?${params.toString()}`);
      setPurchaseOrders(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load purchase orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchaseOrders();
  }, [statusFilter, searchQuery]);

  const viewPO = async (po: PurchaseOrder) => {
    try {
      const full = await apiRequest(`/purchase-orders/${po.id}`);
      setViewingPO(full);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmitPO = async (po: PurchaseOrder) => {
    if (!confirm(`Submit PO ${po.poNumber}?`)) return;
    try {
      await apiRequest(`/purchase-orders/${po.id}/submit`, { method: 'POST' });
      loadPurchaseOrders();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCancelPO = async (po: PurchaseOrder) => {
    if (!confirm(`Cancel PO ${po.poNumber}?`)) return;
    try {
      await apiRequest(`/purchase-orders/${po.id}/cancel`, { method: 'POST' });
      loadPurchaseOrders();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeletePO = async (po: PurchaseOrder) => {
    if (!confirm(`Delete PO ${po.poNumber}? This cannot be undone.`)) return;
    try {
      await apiRequest(`/purchase-orders/${po.id}`, { method: 'DELETE' });
      loadPurchaseOrders();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const openReceiveModal = async (po: PurchaseOrder) => {
    try {
      const full = await apiRequest(`/purchase-orders/${po.id}`);
      setSelectedPO(full);
      setShowReceiveModal(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <PageHeader
        title="Purchase Orders"
        subtitle="Create and manage purchase orders for inventory restocking"
        actions={
          <button
            onClick={() => setShowBuilder(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Purchase Order
          </button>
        }
      />

      <Card>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search PO#, supplier, or part..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="partial">Partial</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {['draft', 'submitted', 'partial', 'received', 'cancelled'].map(status => {
            const count = purchaseOrders.filter(p => p.status === status).length;
            return (
              <div
                key={status}
                className={clsx(
                  'p-3 rounded-lg border cursor-pointer',
                  statusFilter === status ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                )}
                onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              >
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-sm text-gray-600 capitalize">{status}</div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No purchase orders found.</p>
            <p className="text-sm mt-1">Click "New Purchase Order" to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm border-b">
                  <th className="pb-3 font-medium">PO #</th>
                  <th className="pb-3 font-medium">Supplier</th>
                  <th className="pb-3 font-medium">Items</th>
                  <th className="pb-3 font-medium">Total</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Created</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map(po => (
                  <tr key={po.id} className="border-b hover:bg-gray-50">
                    <td className="py-3">
                      <button
                        onClick={() => viewPO(po)}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        {po.poNumber}
                      </button>
                    </td>
                    <td className="py-3">{po.supplierName}</td>
                    <td className="py-3">{po.itemCount || '-'}</td>
                    <td className="py-3 font-medium">${po.totalAmount.toFixed(2)}</td>
                    <td className="py-3">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="py-3 text-gray-600">
                      {new Date(po.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        {po.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleSubmitPO(po)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Submit
                            </button>
                            <button
                              onClick={() => handleDeletePO(po)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {(po.status === 'submitted' || po.status === 'partial') && (
                          <>
                            <button
                              onClick={() => openReceiveModal(po)}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Receive
                            </button>
                            <button
                              onClick={() => handleCancelPO(po)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {po.status === 'cancelled' && (
                          <button
                            onClick={() => handleDeletePO(po)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* PO Builder Modal */}
      <Modal
        isOpen={showBuilder}
        onClose={() => setShowBuilder(false)}
        title="Create Purchase Order"
        size="full"
      >
        <POBuilder
          onSave={() => {
            setShowBuilder(false);
            loadPurchaseOrders();
          }}
          onCancel={() => setShowBuilder(false)}
        />
      </Modal>

      {/* Receive Items Modal */}
      {selectedPO && (
        <Modal
          isOpen={showReceiveModal}
          onClose={() => {
            setShowReceiveModal(false);
            setSelectedPO(null);
          }}
          title={`Receive Items - ${selectedPO.poNumber}`}
          size="xl"
        >
          <ReceiveItemsModal
            po={selectedPO}
            onClose={() => {
              setShowReceiveModal(false);
              setSelectedPO(null);
            }}
            onReceived={() => {
              setShowReceiveModal(false);
              setSelectedPO(null);
              loadPurchaseOrders();
            }}
          />
        </Modal>
      )}

      {/* View PO Modal */}
      {viewingPO && (
        <Modal
          isOpen={!!viewingPO}
          onClose={() => setViewingPO(null)}
          title={`Purchase Order ${viewingPO.poNumber}`}
          size="xl"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-gray-500">Supplier</div>
                <div className="font-medium">{viewingPO.supplierName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Status</div>
                <StatusBadge status={viewingPO.status} />
              </div>
              <div>
                <div className="text-sm text-gray-500">Created</div>
                <div>{new Date(viewingPO.createdAt).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total</div>
                <div className="font-bold text-lg">${viewingPO.totalAmount.toFixed(2)}</div>
              </div>
            </div>

            {viewingPO.notes && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Notes</div>
                <div>{viewingPO.notes}</div>
              </div>
            )}

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2">Part #</th>
                    <th className="text-left px-4 py-2">Description</th>
                    <th className="text-center px-4 py-2">Ordered</th>
                    <th className="text-center px-4 py-2">Received</th>
                    <th className="text-right px-4 py-2">Unit Cost</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {viewingPO.items?.map(item => (
                    <tr key={item.id}>
                      <td className="px-4 py-2 font-medium">{item.partNumber}</td>
                      <td className="px-4 py-2 text-gray-600">{item.description}</td>
                      <td className="px-4 py-2 text-center">{item.qtyOrdered}</td>
                      <td className="px-4 py-2 text-center">
                        <span
                          className={clsx(
                            (item.qtyReceived || 0) >= item.qtyOrdered
                              ? 'text-green-600'
                              : (item.qtyReceived || 0) > 0
                              ? 'text-amber-600'
                              : 'text-gray-400'
                          )}
                        >
                          {item.qtyReceived || 0}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">${item.unitCost.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-medium">${item.lineTotal.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-right font-semibold">
                      Total:
                    </td>
                    <td className="px-4 py-2 text-right font-bold">${viewingPO.totalAmount.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setViewingPO(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
              {(viewingPO.status === 'submitted' || viewingPO.status === 'partial') && (
                <button
                  onClick={() => {
                    setSelectedPO(viewingPO);
                    setViewingPO(null);
                    setShowReceiveModal(true);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Receive Items
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default PurchaseOrdersPage;
